import apiService from '../api'

export interface SalesforceStatus {
  connected: boolean
  username?: string
  instanceUrl?: string
  oauthConfigured: boolean
  oauthRedirectUri?: string
}

export interface SalesforceSyncResult {
  fetched: number
  upserted: number
  since: string
}

export type SalesforceOrderSyncJob = SalesforceSyncResult & {
  status?: 'idle' | 'running' | 'completed' | 'failed'
  processed?: number
  percent?: number
  errorMessage?: string
}

export type SalesforceQcOutcome = 'Accepted' | 'Rejected' | null

export interface SalesforceOrder {
  id: string
  salesforceId: string
  orderNumber?: string | null
  accountName?: string | null
  effectiveDate?: string | null
  status?: string | null
  xeroInvoiceId?: string | null
  invoiceAmount?: string | number | null
  paymentStatus?: string | null
  quantity?: string | number | null
  lot?: string | null
  booking?: string | null
  description?: string | null
  currencyIsoCode?: string | null
  lastSyncedAt: string
  orderFulfillment?: {
    id: string
    outcome: SalesforceQcOutcome
    status: 'Pending' | 'Paid' | 'Completed'
    lotBatch?: string | null
    booking?: string | null
    quantity?: string | null
    stockDispatches?: Array<{
      id: string
      lotNumber?: string | null
      status?: string | null
      warehouse?: { id: string; name: string } | null
    }>
  } | null
}

export interface SalesforceOrdersResponse {
  orders: SalesforceOrder[]
  page: number
  limit: number
  total: number
  totalPages: number
}

export async function getSalesforceStatus(): Promise<SalesforceStatus> {
  const res = await apiService.get<{ success: boolean; data: SalesforceStatus }>('/salesforce/status')
  return (res as any)?.data ?? res
}

export async function getSalesforceAuthUrl(): Promise<string> {
  const res = await apiService.get<{ success: boolean; data: { authUrl: string } }>('/salesforce/auth-url')
  return (res as any)?.data?.authUrl ?? ''
}

export async function disconnectSalesforce(): Promise<{ disconnected: boolean }> {
  const res = await apiService.post<{ success: boolean; data: { disconnected: boolean } }>(
    '/salesforce/disconnect'
  )
  return (res as any)?.data ?? { disconnected: true }
}

export async function startSalesforceOrderSync(): Promise<SalesforceOrderSyncJob> {
  const res = await apiService.post<{ success: boolean; data: SalesforceOrderSyncJob }>(
    '/salesforce/sync/orders',
    undefined,
    { timeout: 30000, suppressGlobalError: true }
  )
  return (res as any)?.data ?? res
}

export async function getSalesforceOrderSyncStatus(): Promise<SalesforceOrderSyncJob> {
  const res = await apiService.get<{ success: boolean; data: SalesforceOrderSyncJob }>(
    '/salesforce/sync/orders/status',
    { suppressGlobalError: true }
  )
  return (res as any)?.data ?? res
}

/** @deprecated Use startSalesforceOrderSync — sync now runs in the background. */
export async function syncSalesforceOrders(): Promise<SalesforceSyncResult> {
  return startSalesforceOrderSync()
}

export async function getSalesforceOrders(params?: {
  page?: number
  limit?: number
  search?: string
  outcome?: 'Pending' | 'Accepted' | 'Rejected'
}): Promise<SalesforceOrdersResponse> {
  const qs = new URLSearchParams()
  if (params?.page) qs.set('page', String(params.page))
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.search) qs.set('search', params.search)
  if (params?.outcome) qs.set('outcome', params.outcome)
  const res = await apiService.get<{ success: boolean; data: SalesforceOrdersResponse }>(
    `/salesforce/orders${qs.size ? `?${qs.toString()}` : ''}`
  )
  return (res as any)?.data ?? res
}

export async function updateSalesforceOrderOutcome(
  id: string,
  outcome: 'Rejected' | null
): Promise<void> {
  await apiService.patch(`/salesforce/orders/${id}/outcome`, { outcome })
}

export async function assignSalesforceOrderLot(id: string, lot: string): Promise<{
  lot: string
  salesforceUpdated: boolean
  orderNumber?: string | null
}> {
  const res = await apiService.patch<{
    success: boolean
    data: { lot: string; salesforceUpdated: boolean; orderNumber?: string | null }
  }>(`/salesforce/orders/${id}/lot`, { lot })
  return (res as any)?.data ?? res
}
